/**
 * Recovering from a deploy that happened under an open tab.
 *
 * Route code is split into content-hashed chunks and loaded on navigation. A
 * deploy replaces the whole set, so a tab that was open across one asks for a
 * filename that no longer exists and gets `TypeError: Failed to fetch
 * dynamically imported module`, which reads like the console is broken when
 * nothing is. One reload always fixes it: index.html is served with
 * `max-age=0`, so the reload picks up the new chunk names.
 *
 * So the first failure reloads, and only a second failure inside the same
 * window is allowed to reach the error boundary, where it gets wording that
 * says what it actually means. The window guard is what stops a genuine outage
 * from becoming an infinite reload loop.
 */

const RELOADED_AT_KEY = 'gjb:chunk-reload-at'

/** Long enough to cover a slow reload, short enough that the next deploy days later reloads again. */
export const RELOAD_WINDOW_MS = 30_000

/**
 * Whether an error is a chunk that failed to load, in any browser's words.
 *
 * Chrome and Safari say "Failed to fetch dynamically imported module" or
 * "Importing a module script failed", Firefox says "error loading dynamically
 * imported module", and webpack-trained readers grep for ChunkLoadError. Vite
 * raises its own wording when the failure is a stylesheet dependency rather
 * than the module itself, and that one has to be here too: it is the same
 * missing-after-deploy fault, and it is thrown before the module is even
 * requested.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : typeof error === 'string' ? error : ''
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(
    message,
  )
}

/**
 * Reload on the first failure, surface the second.
 *
 * A stale tab is fixed by exactly one reload. A failure that survives a reload
 * is something else, usually the network, and reloading again would strand the
 * person in a flicker loop with no error to read.
 */
export function shouldReloadForStaleChunk(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || now - lastReloadAt > RELOAD_WINDOW_MS
}

let reloading = false

/**
 * True once a recovery reload has been asked for.
 *
 * `location.reload()` does not stop the page: the document stays live and
 * keeps rendering for the length of a round trip, which on a slow connection
 * is long enough to read. The boundary uses this to say the console is
 * updating rather than to present the failure as a fault, because by this
 * point it is neither the person's problem nor an unknown one.
 */
export function isReloadInFlight(): boolean {
  return reloading
}

/** sessionStorage throws rather than returning null in some privacy modes. */
function readReloadedAt(): number | null {
  try {
    const stored = Number(window.sessionStorage.getItem(RELOADED_AT_KEY) ?? '')
    return Number.isFinite(stored) && stored > 0 ? stored : null
  } catch {
    return null
  }
}

function rememberReloadedAt(at: number): void {
  try {
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(at))
  } catch {
    // Without the stamp the next failure reloads again rather than explaining
    // itself. That is the safer half of the trade: one extra reload beats
    // refusing to recover at all.
  }
}

/** Wire the recovery up. Called once from the entry point. */
export function installStaleChunkRecovery(): void {
  // Vite raises this for every chunk it fails to import or preload, before the
  // rejection propagates to whatever triggered the navigation.
  window.addEventListener('vite:preloadError', (event) => {
    if (!shouldReloadForStaleChunk(readReloadedAt(), Date.now())) return

    // Deliberately NOT event.preventDefault(). Cancelling the event tells
    // Vite's helper to swallow the error, and its handler then returns
    // undefined out of `baseModule().catch(handlePreloadError)`, which
    // RESOLVES the import with undefined instead of leaving it rejected.
    // React's lazy() reads `.default` off that undefined and throws
    // "Cannot read properties of undefined (reading 'default')", a message
    // nothing can recognise as a missing chunk. Cancelling would therefore
    // replace the one error this file exists to explain with an unreadable
    // one, on screen for the whole length of the reload. Letting Vite rethrow
    // keeps the real error, and `isReloadInFlight` is what tells the boundary
    // a fix is already on its way.
    reloading = true
    rememberReloadedAt(Date.now())
    window.location.reload()
  })
}
